import React from 'react';

const stroke = '#8b5cf6';
const dim = '#3f3f46';
const faint = '#27272a';
const txt = '#71717a';

export const VoiceVisual: React.FC = () => {
  const bars = [12, 26, 40, 22, 52, 34, 62, 44, 28, 70, 48, 30, 56, 38, 20, 46, 32, 16];
  return (
    <svg viewBox="0 0 320 110" className="w-full h-full" preserveAspectRatio="xMidYMid slice" fill="none">
      <rect x="8" y="14" width="304" height="82" rx="10" stroke={faint} />
      <circle cx="30" cy="55" r="14" stroke={stroke} strokeWidth="1.5" />
      <path d="M26 55h8M30 51v8" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" />
      {bars.map((h, i) => (
        <line key={i} x1={58 + i * 8.5} y1={55 - h / 2} x2={58 + i * 8.5} y2={55 + h / 2} stroke={i % 4 === 1 ? stroke : dim} strokeWidth="3" strokeLinecap="round" />
      ))}
      <path d="M60 84h180" stroke={faint} strokeWidth="1" />
      <text x="245" y="86" fontFamily="monospace" fontSize="9" fill={txt}>call · live</text>
      <rect x="240" y="38" width="56" height="30" rx="6" stroke={faint} />
      <circle cx="252" cy="53" r="3" fill={stroke} />
      <text x="260" y="56" fontFamily="monospace" fontSize="8" fill={txt}>booked</text>
    </svg>
  );
};

export const PosVisual: React.FC = () => {
  const rows = [30, 46, 62];
  const bars = [2, 1, 3, 1, 2, 4, 1, 2, 1, 3, 2, 1, 2, 3, 1, 2];
  let bx = 240;
  return (
    <svg viewBox="0 0 320 110" className="w-full h-full" preserveAspectRatio="xMidYMid slice" fill="none">
      {/* terminal stand */}
      <path d="M74 90 l-8 12 h44 l-8 -12" stroke={dim} />
      <path d="M58 102h58" stroke={dim} strokeLinecap="round" />
      {/* terminal body */}
      <rect x="14" y="14" width="132" height="78" rx="7" stroke={dim} />
      <rect x="21" y="21" width="118" height="64" rx="4" stroke={faint} />
      {/* cart rows */}
      {rows.map((y, i) => (
        <g key={i}>
          <rect x="26" y={y - 5} width="10" height="10" rx="2.5" stroke={i === 0 ? stroke : dim} />
          <path d={`M40 ${y - 2}h46M40 ${y + 2}h30`} stroke={faint} strokeWidth="1.6" strokeLinecap="round" />
          <circle cx="100" cy={y} r="4" stroke={i === 0 ? stroke : dim} />
          <path d={`M98 ${y}h4M100 ${y - 2}v4`} stroke={i === 0 ? stroke : dim} strokeWidth="0.9" />
        </g>
      ))}
      {/* totals + confirm */}
      <path d="M112 28h24M112 38h24M112 48h24" stroke={faint} strokeWidth="1.6" strokeLinecap="round" />
      <rect x="110" y="66" width="28" height="13" rx="4" stroke={stroke} />
      <path d="M118 72.5l3 3 5.5 -5.5" stroke={stroke} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      {/* receipt */}
      <path d="M172 92 V22 L176 18 L180 22 L184 18 L188 22 L192 18 L196 22 L200 18 L204 22 L204 92 Z" stroke={dim} />
      <circle cx="188" cy="30" r="3.5" stroke={dim} />
      {[42, 48, 54].map((y) => (
        <path key={y} d={`M178 ${y}h20`} stroke={faint} strokeWidth="1.6" strokeLinecap="round" />
      ))}
      <path d="M176 62h24" stroke={faint} strokeDasharray="3 2" />
      <path d="M178 70h20" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      {/* barcode + scan brackets */}
      <path d="M228 44 v-6 a4 4 0 0 1 4 -4 h6 M298 34 h6 a4 4 0 0 1 4 4 v6 M228 58 v6 a4 4 0 0 0 4 4 h6 M304 64 h-6 a4 4 0 0 1 -4 -4 v-6" stroke={stroke} strokeWidth="1.2" />
      {bars.map((w, i) => {
        const x = bx; bx += w + 3;
        return <line key={i} x1={x} y1="40" x2={x} y2="62" stroke={i % 4 === 2 ? stroke : dim} strokeWidth={w * 0.8} />;
      })}
      <path d="M232 51h72" stroke={stroke} strokeWidth="1" opacity="0.9" />
      {/* dashed link terminal -> scan */}
      <path d="M146 40 C 170 34, 190 34, 212 40" stroke={dim} strokeDasharray="3 3" opacity="0.6" />
      {/* floating product chip */}
      <rect x="234" y="76" width="40" height="22" rx="5" stroke={dim} />
      <path d="M240 84h10M240 90h16" stroke={faint} strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="266" cy="87" r="4.5" stroke={stroke} />
      <path d="M264.5 87l1.5 1.5 2.5 -2.5" stroke={stroke} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const RagVisual: React.FC = () => {
  const envelopes = [18, 48, 78];
  const pills = [18, 48, 78];
  return (
    <svg viewBox="0 0 320 110" className="w-full h-full" preserveAspectRatio="xMidYMid slice" fill="none">
      {/* envelopes -> node */}
      {envelopes.map((y, i) => (
        <g key={i}>
          <rect x={22 + i * 6} y={y - 9} width="26" height="18" rx="2" stroke={i === 1 ? stroke : dim} />
          <path d={`M${22 + i * 6} ${y - 9} l13 9 l13 -9`} stroke={i === 1 ? stroke : dim} />
          <path d={`M${48 + i * 6} ${y} C 90 ${y}, 110 55, 144 55`} stroke={stroke} strokeWidth="1" opacity="0.7" />
        </g>
      ))}
      {/* neural node */}
      <circle cx="160" cy="55" r="17" stroke={stroke} strokeWidth="1.5" />
      {[[160, 47], [154, 53], [166, 53], [157, 61], [164, 61], [160, 55]].map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.8" fill={i === 5 ? stroke : '#c4b5fd'} />
      ))}
      <path d="M160 47l-6 6m6-6l6 6m-12 0l3 8m9-8l-3 8m-6-6h6" stroke={dim} strokeWidth="0.8" />
      <circle cx="140" cy="36" r="1.2" fill={dim} /><circle cx="182" cy="40" r="1.2" fill={dim} /><circle cx="178" cy="74" r="1.2" fill={dim} />
      {/* node -> pills */}
      {pills.map((y, i) => (
        <path key={i} d={`M177 55 C 200 55, 196 ${y + 11}, 214 ${y + 11}`} stroke={stroke} strokeWidth="1" opacity="0.7" />
      ))}
      {/* category pills */}
      {pills.map((y, i) => (
        <g key={i}>
          <circle cx="218" cy={y + 11} r="2" fill={stroke} />
          <rect x="222" y={y} width="90" height="22" rx="11" stroke={i === 1 ? stroke : dim} />
          <path d={`M${290} ${y + 8}h14M${290} ${y + 13}h10`} stroke={faint} strokeWidth="2" strokeLinecap="round" />
          {i === 0 && <g stroke={stroke} strokeWidth="1.2"><rect x="230" y={y + 6} width="13" height="10" rx="2" /><path d={`M230 ${y + 9}h13`} /></g>}
          {i === 1 && <g stroke={stroke} strokeWidth="1.2"><path d={`M231 ${y + 13} a6.5 6.5 0 0 1 13 0`} /><path d={`M231 ${y + 13}v3h3v-3zM241 ${y + 13}v3h3v-3z`} /></g>}
          {i === 2 && <g stroke={stroke} strokeWidth="1.4"><path d={`M231 ${y + 15}v-6M236 ${y + 15}v-9M241 ${y + 15}v-4`} strokeLinecap="round" /></g>}
        </g>
      ))}
    </svg>
  );
};

export const PlatformVisual: React.FC = () => {
  return (
    <svg viewBox="0 0 320 110" className="w-full h-full" preserveAspectRatio="xMidYMid slice" fill="none">
      <rect x="20" y="20" width="200" height="70" rx="8" stroke={dim} />
      <circle cx="32" cy="30" r="2.5" fill={faint} /><circle cx="41" cy="30" r="2.5" fill={faint} /><circle cx="50" cy="30" r="2.5" fill={faint} />
      <path d="M20 36h200" stroke={faint} />
      <path d="M34 52h60M34 62h100" stroke={faint} strokeWidth="2" strokeLinecap="round" />
      <rect x="34" y="70" width="46" height="12" rx="6" stroke={stroke} />
      <text x="41" y="79" fontFamily="monospace" fontSize="8" fill={stroke}>deploy</text>
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={246} y={18 + i * 26} width="56" height="20" rx="5" stroke={dim} />
          <path d={`M253 ${28 + i * 26}h36`} stroke={faint} strokeWidth="2" strokeLinecap="round" />
        </g>
      ))}
      <path d="M220 55 C 234 55, 234 28, 246 28 M220 55h26 M220 55 C 234 55, 234 82, 246 82" stroke={stroke} strokeWidth="1.2" strokeDasharray="3 3" />
      <circle cx="220" cy="55" r="4" stroke={stroke} strokeWidth="1.5" />
    </svg>
  );
};
